import React from "react";
import { Sun, ShieldCheck, Compass, ArrowRight } from "lucide-react";
import MountainRidge from "./MountainRidge";

const OUTCOMES = [
  {
    icon: Sun,
    title: "Energie statt Erschöpfung",
    text: "Du gehst durch den Tag, ohne nachmittags einzubrechen. Dein Körper hat, was er braucht – nicht nur Bewegung, sondern auch die richtigen Nährstoffe.",
  },
  {
    icon: ShieldCheck,
    title: "Beschwerdefrei trainieren",
    text: "Schmerzen, die jahrelang geblieben sind, werden an der Wurzel angegangen statt nur umgangen.",
  },
  {
    icon: Compass,
    title: "Ein Ansprechpartner, ein klarer Plan",
    text: "Kein Hin- und Herspringen zwischen Experten mehr. Training und Mikronährstoffe aus einer Hand, abgestimmt auf dich.",
  },
];

function scrollTo(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function OutcomeSection() {
  return (
    <section
      id="outcome-section"
      data-testid="outcome-section"
      className="relative bg-[#1F2937] text-[#F7F9FB] py-24 lg:py-32 overflow-hidden"
    >
      {/* Decorative ridges */}
      <div className="absolute top-0 inset-x-0 opacity-30">
        <MountainRidge fill="#1F2937" bg="transparent" accent="#6FB04F" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-16">
          <div className="lg:col-span-7">
            <p className="text-xs sm:text-sm font-semibold tracking-[0.18em] uppercase text-[#6FB04F] mb-5">
              Wenn alles ineinandergreift
            </p>
            <h2
              data-testid="outcome-title"
              className="font-display text-3xl sm:text-4xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]"
            >
              Stell dir vor, du fühlst dich{" "}
              <span className="text-[#2E89C4]">wieder wirklich fit.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base lg:text-lg text-[#C7CFD9] leading-relaxed">
              Nicht „fit genug, um durchzuhalten&ldquo; – sondern wach, kraftvoll und
              frei von dem, was dich seit Jahren bremst.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {OUTCOMES.map((o, i) => {
            const Icon = o.icon;
            return (
              <div
                key={i}
                data-testid={`outcome-card-${i}`}
                className="group relative rounded-2xl border border-[#F7F9FB]/10 bg-[#F7F9FB]/[0.04] p-7 lg:p-8 transition-all duration-300 hover:-translate-y-1 hover:bg-[#F7F9FB]/[0.08] hover:border-[#2E89C4]/40"
              >
                <div className="h-12 w-12 rounded-xl bg-[#6FB04F]/15 flex items-center justify-center mb-6">
                  <Icon size={22} className="text-[#6FB04F]" strokeWidth={1.8} />
                </div>
                <h3 className="font-display text-xl font-bold mb-3 text-[#F7F9FB]">
                  {o.title}
                </h3>
                <p className="text-[15px] leading-relaxed text-[#F7F9FB]/70">
                  {o.text}
                </p>
              </div>
            );
          })}
        </div>

        {/* New Paradigm */}
        <div
          data-testid="new-paradigm"
          className="mt-20 lg:mt-24 rounded-3xl border border-[#F7F9FB]/10 bg-gradient-to-br from-[#F7F9FB]/[0.06] to-transparent p-8 lg:p-14"
        >
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-9">
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#2E89C4] mb-4">
                Das neue Paradigma
              </p>
              <p className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold leading-[1.2] tracking-tight">
                Ganzheitliche Gesundheit heißt: Training und Mikronährstoffe
                werden nicht{" "}
                <span className="text-[#6FB04F]">getrennt</span> betrachtet –
                sondern <span className="text-[#2E89C4]">gemeinsam.</span>
              </p>
              <p className="mt-5 text-[#F7F9FB]/65 lg:text-lg max-w-2xl">
                Mit einer Person, die beide Seiten wirklich versteht.
              </p>
            </div>
            <div className="lg:col-span-3 lg:text-right">
              <button
                data-testid="outcome-cta-button"
                onClick={() => scrollTo("booking-section")}
                className="btn-primary inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold"
              >
                Jetzt Erstgespräch buchen
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
